// Workout Cache - Store routine data for instant workout loading
// This enables starting a workout without waiting for server fetch
// Uses cross-platform storage (native on Android/iOS, localStorage on web)

import * as storage from "@/lib/storage";
import type { ProgramExerciseVariantConfig } from "@/lib/programs/variants";
import type { PeriodizationWeeklyOverride } from "@/types/database";
import {
  getScopedStorageKey,
  getScopedStorageKeyCandidates,
} from "@/lib/sync/storage-scope";

const WORKOUT_CACHE_KEY = "pendingWorkoutData";

export type CachedExercise = {
  id: string;
  name: string;
  muscle_groups: string[];
  equipment?: string | null;
  demo_video_url: string | null;
  external_link: string | null;
};

export type CachedProgramExercise = {
  id: string;
  order_index: number;
  training_type?: string | null;
  sets: number | null;
  warmup_sets?: number | null;
  reps: string | null;
  weight_prescription: string | null;
  rest_seconds: number | null;
  target_duration_seconds?: number | null;
  target_distance_meters?: number | null;
  cardio_params?: Record<string, unknown> | null;
  target_rounds?: number | null;
  work_seconds?: number | null;
  rest_between_rounds_seconds?: number | null;
  reps_per_round?: string | null;
  interval_params?: Record<string, unknown> | null;
  notes: string | null;
  default_setup_signature?: string | null;
  default_setup_json?: Partial<Record<"pin" | "width" | "grip" | "axis" | "foot_position", string>> | null;
  variant_signature?: string | null;
  variant_config?: ProgramExerciseVariantConfig | null;
  exercises: CachedExercise | null;
  // Periodization fields (optional, only for periodized programs)
  periodization_enabled?: boolean | null;
  estimated_1rm?: number | null;
  estimated_1rm_unit?: string | null;
  start_rpe_low?: number | null;
  start_rpe_high?: number | null;
  end_rpe_low?: number | null;
  end_rpe_high?: number | null;
  weekly_overrides?: PeriodizationWeeklyOverride[];
};

export type CachedWeeklyTarget = {
  rpeLow: number;
  rpeHigh: number;
  weightLow: number;
  weightHigh: number;
  sets: number;
  reps: number;
  isDeload: boolean;
  backoffSets?: number;
  backoffRpeLow?: number;
  backoffRpeHigh?: number;
  backoffWeightLow?: number;
  backoffWeightHigh?: number;
  backoffReps?: number;
};

export type CachedPeriodizationConfig = {
  programType: "periodized";
  currentWeek: number;
  totalWeeks: number;
  progressionStyle: string;
  deloadEveryNWeeks: number | null;
  startRpeDefault: number;
  endRpeDefault: number;
  topSetRepsDefault?: number | null;
  backoffSetsDefault?: number;
  backoffStartRpeDefault?: number | null;
  backoffEndRpeDefault?: number | null;
  backoffRepsDefault?: number | null;
};

export type CachedWorkoutData = {
  sessionId?: string; // Client-generated UUID for instant start
  sessionStartedAt?: string | null;
  routineId: string | null;
  routineName: string;
  clientProgramId: string | null;
  defaultWorkSets?: number | null;
  defaultWarmupSets?: number | null;
  exercises: CachedProgramExercise[];
  cachedAt: string;
  // Periodization data (only for periodized programs)
  periodizationConfig?: CachedPeriodizationConfig;
  weeklyTargets?: Record<string, CachedWeeklyTarget>; // programExerciseId -> target
  currentWeek?: number;
  totalWeeks?: number;
};

function parseCachedWorkoutSnapshot(raw: string | null): CachedWorkoutData | null {
  if (!raw) return null;

  try {
    return JSON.parse(raw) as CachedWorkoutData;
  } catch {
    return null;
  }
}

function getCachedWorkoutRank(data: CachedWorkoutData): number {
  const cachedAtMs = new Date(data.cachedAt).getTime();
  return Number.isFinite(cachedAtMs) ? cachedAtMs : -Infinity;
}

function pickPreferredCachedWorkout(
  snapshots: Array<{ key: string; data: CachedWorkoutData }>
): { key: string; data: CachedWorkoutData } | null {
  let best: { key: string; data: CachedWorkoutData } | null = null;
  let bestRank = -Infinity;

  for (const snapshot of snapshots) {
    const rank = getCachedWorkoutRank(snapshot.data);
    if (rank > bestRank) {
      best = snapshot;
      bestRank = rank;
    }
  }

  return best;
}

async function loadPreferredCachedWorkoutAsync(): Promise<{ key: string; data: CachedWorkoutData } | null> {
  const snapshots: Array<{ key: string; data: CachedWorkoutData }> = [];
  for (const key of getScopedStorageKeyCandidates(WORKOUT_CACHE_KEY)) {
    const parsed = parseCachedWorkoutSnapshot(await storage.getItem(key));
    if (parsed) {
      snapshots.push({ key, data: parsed });
    }
  }
  return pickPreferredCachedWorkout(snapshots);
}

function loadPreferredCachedWorkoutSync(): { key: string; data: CachedWorkoutData } | null {
  const snapshots: Array<{ key: string; data: CachedWorkoutData }> = [];
  for (const key of getScopedStorageKeyCandidates(WORKOUT_CACHE_KEY)) {
    const parsed = parseCachedWorkoutSnapshot(localStorage.getItem(key));
    if (parsed) {
      snapshots.push({ key, data: parsed });
    }
  }
  return pickPreferredCachedWorkout(snapshots);
}

async function cleanupScopedWorkoutCacheFallbacksAsync(): Promise<void> {
  const [primaryKey, ...fallbackKeys] = getScopedStorageKeyCandidates(WORKOUT_CACHE_KEY);
  await Promise.all(
    fallbackKeys
      .filter((key) => key !== primaryKey)
      .map((key) => storage.removeItem(key))
  );
}

function cleanupScopedWorkoutCacheFallbacksSync(): void {
  const [primaryKey, ...fallbackKeys] = getScopedStorageKeyCandidates(WORKOUT_CACHE_KEY);
  for (const key of fallbackKeys) {
    if (key === primaryKey) continue;
    localStorage.removeItem(key);
  }
}

// Cache workout data before starting (for instant loading)
export async function cacheWorkoutData(data: Omit<CachedWorkoutData, "cachedAt">): Promise<void> {
  if (typeof window === "undefined") return;

  const t0 = performance.now();
  if (typeof window !== "undefined") {
    console.log("[Start workout] cacheWorkoutData: write start", { sessionId: data.sessionId?.slice(0, 8) });
  }
  try {
    await storage.setJSON(getScopedStorageKey(WORKOUT_CACHE_KEY), {
      ...data,
      cachedAt: new Date().toISOString(),
    });
    await cleanupScopedWorkoutCacheFallbacksAsync();
    if (typeof window !== "undefined") {
      console.log("[Start workout] cacheWorkoutData: write done", { ms: (performance.now() - t0).toFixed(0) });
    }
  } catch (error) {
    console.error("Failed to cache workout data:", error);
  }
}

export async function reconcileCachedWorkoutSessionIdAsync(
  oldSessionId: string,
  newSessionId: string
): Promise<void> {
  const cached = await getCachedWorkoutData();
  if (!cached || cached.sessionId !== oldSessionId) return;
  await cacheWorkoutData({ ...cached, sessionId: newSessionId });
}

// Get cached workout data
// No TTL — cache lives until workout completion, explicit clear, or sign-out
export async function getCachedWorkoutData(): Promise<CachedWorkoutData | null> {
  if (typeof window === "undefined") return null;

  try {
    const snapshot = await loadPreferredCachedWorkoutAsync();
    if (!snapshot) return null;
    const storageKey = getScopedStorageKey(WORKOUT_CACHE_KEY);
    if (snapshot.key !== storageKey) {
      await storage.setJSON(storageKey, snapshot.data);
    }
    await cleanupScopedWorkoutCacheFallbacksAsync();
    return snapshot.data;
  } catch (error) {
    console.error("Failed to get cached workout data:", error);
    return null;
  }
}

// Clear the workout cache
export async function clearWorkoutCache(): Promise<void> {
  if (typeof window === "undefined") return;

  try {
    await Promise.all([
      ...getScopedStorageKeyCandidates(WORKOUT_CACHE_KEY).map((key) => storage.removeItem(key)),
      storage.removeItem(WORKOUT_CACHE_KEY),
    ]);
  } catch (error) {
    console.error("Failed to clear workout cache:", error);
  }
}

// Check if there's cached data for a specific routine
export async function hasCachedWorkout(routineId: string): Promise<boolean> {
  const cached = await getCachedWorkoutData();
  return cached?.routineId === routineId;
}

// Synchronous version for loading states (web only, returns null on native)
// Use this only when you need immediate data without async
// No TTL — cache lives until workout completion, explicit clear, or sign-out
export function getCachedWorkoutDataSync(): CachedWorkoutData | null {
  if (typeof window === "undefined") return null;
  
  // On native, we can't read synchronously - return null
  // The async version will be used once the component hydrates
  try {
    // Check if running in Capacitor native
    if ((window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor?.isNativePlatform?.()) {
      return null;
    }
    
    const snapshot = loadPreferredCachedWorkoutSync();
    if (!snapshot) return null;
    const storageKey = getScopedStorageKey(WORKOUT_CACHE_KEY);
    if (snapshot.key !== storageKey) {
      localStorage.setItem(storageKey, JSON.stringify(snapshot.data));
    }
    cleanupScopedWorkoutCacheFallbacksSync();

    return snapshot.data;
  } catch {
    return null;
  }
}

// ============================================
// ALL ROUTINES CACHE - Store all routines on sign-in
// ============================================

const ALL_ROUTINES_CACHE_KEY = "allRoutinesCache";

export type CachedRoutine = {
  id: string;
  name: string;
  clientProgramId: string;
  exercises: CachedProgramExercise[];
};

type AllRoutinesCache = {
  routines: Record<string, CachedRoutine>;
  cachedAt: string;
};

// Cache all routines from programs data
export async function cacheAllRoutines(
  programs: Array<{
    assignment: { id: string };
    routines: Array<{
      id: string;
      name: string | null;
      day_number: number;
        exercises: Array<{
          id: string;
          order_index?: number;
          training_type?: string | null;
          sets: number | null;
          warmup_sets?: number | null;
          reps: string | null;
          weight_prescription: string | null;
          rest_seconds: number | null;
          target_duration_seconds?: number | null;
          target_distance_meters?: number | null;
          cardio_params?: Record<string, unknown> | null;
          target_rounds?: number | null;
          work_seconds?: number | null;
          rest_between_rounds_seconds?: number | null;
          reps_per_round?: string | null;
          interval_params?: Record<string, unknown> | null;
          notes: string | null;
          default_setup_signature?: string | null;
          default_setup_json?: Partial<Record<"pin" | "width" | "grip" | "axis" | "foot_position", string>> | null;
          variant_signature?: string | null;
          variant_config?: ProgramExerciseVariantConfig | null;
          periodization_enabled?: boolean | null;
          estimated_1rm?: number | null;
          estimated_1rm_unit?: string | null;
          start_rpe_low?: number | null;
          start_rpe_high?: number | null;
          end_rpe_low?: number | null;
          end_rpe_high?: number | null;
          weekly_overrides?: PeriodizationWeeklyOverride[];
        exercises: {
          id: string;
          name: string;
          muscle_groups: string[];
          equipment?: string | null;
          demo_video_url?: string | null;
          external_link?: string | null;
        } | null;
      }>;
    }>;
  }>
): Promise<void> {
  if (typeof window === "undefined") return;

  try {
    const cache: AllRoutinesCache = {
      routines: {},
      cachedAt: new Date().toISOString(),
    };

    for (const program of programs) {
      for (const routine of program.routines) {
        cache.routines[routine.id] = {
          id: routine.id,
          name: routine.name || `Day ${routine.day_number}`,
          clientProgramId: program.assignment.id,
          exercises: routine.exercises.map((ex, index) => ({
            id: ex.id,
            order_index: ex.order_index ?? index,
            training_type: ex.training_type ?? null,
            sets: ex.sets,
            warmup_sets: ex.warmup_sets ?? null,
            reps: ex.reps,
            weight_prescription: ex.weight_prescription,
            rest_seconds: ex.rest_seconds,
            target_duration_seconds: ex.target_duration_seconds ?? null,
            target_distance_meters: ex.target_distance_meters ?? null,
            cardio_params: ex.cardio_params ?? null,
            target_rounds: ex.target_rounds ?? null,
            work_seconds: ex.work_seconds ?? null,
            rest_between_rounds_seconds: ex.rest_between_rounds_seconds ?? null,
            reps_per_round: ex.reps_per_round ?? null,
            interval_params: ex.interval_params ?? null,
            notes: ex.notes,
            default_setup_signature: ex.default_setup_signature ?? null,
            default_setup_json: ex.default_setup_json ?? null,
            variant_signature: ex.variant_signature ?? null,
            variant_config: ex.variant_config ?? null,
            periodization_enabled: ex.periodization_enabled ?? null,
            estimated_1rm: ex.estimated_1rm ?? null,
            estimated_1rm_unit: ex.estimated_1rm_unit ?? null,
            start_rpe_low: ex.start_rpe_low ?? null,
            start_rpe_high: ex.start_rpe_high ?? null,
            end_rpe_low: ex.end_rpe_low ?? null,
            end_rpe_high: ex.end_rpe_high ?? null,
            weekly_overrides: ex.weekly_overrides ?? [],
            exercises: ex.exercises ? {
              id: ex.exercises.id,
              name: ex.exercises.name,
              muscle_groups: ex.exercises.muscle_groups || [],
              equipment: ex.exercises.equipment ?? null,
              demo_video_url: ex.exercises.demo_video_url || null,
              external_link: ex.exercises.external_link || null,
            } : null,
          })),
        };
      }
    }

    await storage.setJSON(getScopedStorageKey(ALL_ROUTINES_CACHE_KEY), cache);
  } catch (error) {
    console.error("Failed to cache all routines:", error);
  }
}

// Get a specific routine from the cache
export async function getCachedRoutine(routineId: string): Promise<CachedRoutine | null> {
  if (typeof window === "undefined") return null;

  try {
    const cache = await storage.getJSON<AllRoutinesCache>(getScopedStorageKey(ALL_ROUTINES_CACHE_KEY));
    if (!cache) return null;
    return cache.routines[routineId] || null;
  } catch (error) {
    console.error("Failed to get cached routine:", error);
    return null;
  }
}

// Clear all routines cache
export async function clearAllRoutinesCache(): Promise<void> {
  if (typeof window === "undefined") return;

  try {
    await Promise.all([
      storage.removeItem(getScopedStorageKey(ALL_ROUTINES_CACHE_KEY)),
      storage.removeItem(ALL_ROUTINES_CACHE_KEY),
    ]);
  } catch (error) {
    console.error("Failed to clear all routines cache:", error);
  }
}

// ============================================
// FULL ACCOUNT DATA CACHE
// Stores all user data on sign-in for offline/instant access
// ============================================

const ACCOUNT_CACHE_PREFIX = "accountCache_";

type CacheKeys =
  | "history"
  | "exercises"
  | "weightLogs"
  | "nutritionLogs"
  | "customFoods"
  | "foodHistory"
  | "mealTemplates"
  | "measurements"
  | "settings"
  | "profile"
  | "stats"
  | "programSummaries"
  | "prStarsSummary"
  | "trainingAnalysis"
  | "calorieTarget"
  | "calorieTargetHistory";

const syncCacheMemo = new Map<string, { result: unknown; expiry: number }>();

type AccountCacheEntry<T> = {
  data: T;
  cachedAt: string;
};

function writeSyncAccountCacheMirror<T>(key: CacheKeys, data: T, cachedAtIso: string) {
  if (typeof window === "undefined") return;

  const fullKey = getScopedStorageKey(ACCOUNT_CACHE_PREFIX + key);
  const now = Date.now();
  const cachedAt = new Date(cachedAtIso).getTime();

  try {
    localStorage.setItem(fullKey, JSON.stringify({
      data,
      cachedAt: cachedAtIso,
    }));
  } catch {
    // Best-effort sync mirror only.
  }

  syncCacheMemo.set(fullKey, {
    result: {
      data,
      cachedAt: Number.isNaN(cachedAt) ? 0 : cachedAt,
    },
    expiry: now + 5000,
  });
}

function clearSyncAccountCacheMirror(key: CacheKeys) {
  if (typeof window === "undefined") return;

  const fullKey = getScopedStorageKey(ACCOUNT_CACHE_PREFIX + key);
  const legacyKey = ACCOUNT_CACHE_PREFIX + key;
  try {
    localStorage.removeItem(fullKey);
    localStorage.removeItem(legacyKey);
  } catch {
    // Best-effort sync mirror only.
  }
  syncCacheMemo.delete(fullKey);
  syncCacheMemo.delete(legacyKey);
}

function parseAccountCacheEntry<T>(value: string | null): AccountCacheEntry<T> | null {
  if (!value) return null;

  try {
    const parsed = JSON.parse(value) as AccountCacheEntry<T>;
    if (!parsed?.data) return null;
    return parsed;
  } catch {
    return null;
  }
}

function buildSyncAccountCacheResult<T>(entry: AccountCacheEntry<T>) {
  const cachedAt = new Date(entry.cachedAt).getTime();
  return {
    data: entry.data,
    cachedAt: Number.isNaN(cachedAt) ? 0 : cachedAt,
  };
}

// Generic cache functions for account data
export async function cacheAccountData<T>(key: CacheKeys, data: T): Promise<void> {
  if (typeof window === "undefined") return;

  try {
    const cachedAtIso = new Date().toISOString();
    writeSyncAccountCacheMirror(key, data, cachedAtIso);
    await storage.setJSON(getScopedStorageKey(ACCOUNT_CACHE_PREFIX + key), {
      data,
      cachedAt: cachedAtIso,
    });
  } catch (error) {
    console.error(`Failed to cache ${key}:`, error);
  }
}

export async function getAccountCache<T>(key: CacheKeys): Promise<T | null> {
  if (typeof window === "undefined") return null;

  const primaryKey = getScopedStorageKey(ACCOUNT_CACHE_PREFIX + key);
  try {
    for (const candidateKey of getScopedStorageKeyCandidates(ACCOUNT_CACHE_PREFIX + key)) {
      const parsed = await storage.getJSON<AccountCacheEntry<T>>(candidateKey);
      if (!parsed?.data) continue;

      if (candidateKey !== primaryKey) {
        writeSyncAccountCacheMirror(key, parsed.data, parsed.cachedAt);
        await storage.setJSON(primaryKey, parsed);
      }

      return parsed.data;
    }

    return null;
  } catch (error) {
    console.error(`Failed to get cached ${key}:`, error);
    return null;
  }
}

export async function clearAllAccountCache(): Promise<void> {
  if (typeof window === "undefined") return;

  try {
    const keys: CacheKeys[] = [
      "history",
      "exercises",
      "weightLogs",
      "nutritionLogs",
      "customFoods",
      "foodHistory",
      "mealTemplates",
      "measurements",
      "settings",
      "profile",
      "stats",
      "programSummaries",
      "prStarsSummary",
      "trainingAnalysis",
    ];
    await Promise.all(
      keys.flatMap((key) => [
        storage.removeItem(getScopedStorageKey(ACCOUNT_CACHE_PREFIX + key)),
        storage.removeItem(ACCOUNT_CACHE_PREFIX + key),
      ])
    );
    keys.forEach((key) => clearSyncAccountCacheMirror(key));
  } catch (error) {
    console.error("Failed to clear account cache:", error);
  }
}

// ============================================
// SYNCHRONOUS CACHE READER (localStorage mirror)
// For use as TanStack Query initialData — must be sync.
// ============================================

export function getAccountCacheSync<T>(key: CacheKeys): { data: T; cachedAt: number } | null {
  if (typeof window === "undefined") return null;

  const fullKey = getScopedStorageKey(ACCOUNT_CACHE_PREFIX + key);
  const now = Date.now();
  const memo = syncCacheMemo.get(fullKey);
  if (memo && memo.expiry > now) {
    return memo.result as { data: T; cachedAt: number } | null;
  }

  try {
    for (const candidateKey of getScopedStorageKeyCandidates(ACCOUNT_CACHE_PREFIX + key)) {
      const parsed = parseAccountCacheEntry<T>(localStorage.getItem(candidateKey));
      if (!parsed) continue;

      if (candidateKey !== fullKey) {
        try {
          localStorage.setItem(fullKey, JSON.stringify(parsed));
        } catch {
          // Best-effort sync mirror only.
        }
      }

      const result = buildSyncAccountCacheResult(parsed);
      syncCacheMemo.set(fullKey, { result, expiry: now + 5000 });
      return result;
    }

    syncCacheMemo.set(fullKey, { result: null, expiry: now + 5000 });
    return null;
  } catch {
    syncCacheMemo.set(fullKey, { result: null, expiry: now + 5000 });
    return null;
  }
}

// Get cache stats for debugging
export async function getAccountCacheStats(): Promise<{ totalSize: number; items: Record<string, number> }> {
  if (typeof window === "undefined") return { totalSize: 0, items: {} };

  // Use web storage size calculation (native doesn't expose size easily)
  const totalSize = await storage.getStorageSize();
  
  return { totalSize, items: {} };
}
